'use client';

import React, { useRef, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { JobCardFormData } from './JobCardWizard';
import { PenLine, RotateCcw, CheckCircle, FileText, AlertTriangle } from 'lucide-react';

interface Step4Props {
  submitting: boolean;
}

export default function Step4SignatureSubmit({ submitting }: Step4Props) {
  const { register, setValue, watch, formState: { errors } } = useFormContext<JobCardFormData>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const watchedValues = watch(['vehicleReg', 'technician', 'branch', 'date', 'jobNumber']);
  const [vehicleReg, technician, branch, date, jobNumber] = watchedValues;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e, canvas);
    setLastPos(pos);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setLastPos(pos);
    setHasSig(true);
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    setValue('signatureData', dataUrl);
  };

  const clearSig = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
    setValue('signatureData', '');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center">
          <PenLine size={18} className="text-positive" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Branch Representative Sign-Off</h2>
          <p className="text-xs text-muted-foreground">Review the summary, sign, and submit to generate the PDF report</p>
        </div>
      </div>

      {/* Summary card */}
      <div className="p-4 rounded-xl border border-border bg-secondary/30">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={15} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Job Card Summary</p>
          <span className="font-mono-data text-xs badge badge-info">{jobNumber}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          {[
            { label: 'Vehicle Reg', value: vehicleReg || '—', mono: true },
            { label: 'Branch', value: branch || '—', mono: false },
            { label: 'Technician', value: technician || '—', mono: false },
            { label: 'Date', value: date || '—', mono: true },
          ].map((item) => (
            <div key={`summary-${item.label}`}>
              <p className="text-muted-foreground uppercase tracking-widest mb-0.5" style={{ fontSize: '10px' }}>{item.label}</p>
              <p className={`font-semibold text-foreground ${item.mono ? 'font-mono-data' : ''}`}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Rep name */}
      <div>
        <label className="label-field">Branch Representative Name</label>
        <input
          {...register('branchRepName', { required: 'Branch representative name is required for sign-off' })}
          className="input-field"
          placeholder="Full name of the authorising representative"
        />
        {errors.branchRepName && <p className="field-error">{errors.branchRepName.message}</p>}
        <p className="field-hint">This name will appear on the generated PDF alongside the signature</p>
      </div>

      {/* Signature pad */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label-field flex items-center gap-1.5">
            <PenLine size={13} />
            Authorisation Signature
            <span className="badge badge-danger text-xs ml-1">Required</span>
          </label>
          {hasSig && (
            <button
              type="button"
              onClick={clearSig}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-danger transition-colors duration-150"
            >
              <RotateCcw size={12} />
              Clear signature
            </button>
          )}
        </div>

        <div className={`sig-canvas-wrap relative ${hasSig ? 'border-primary' : ''}`}>
          <canvas
            ref={canvasRef}
            width={700}
            height={180}
            className="w-full rounded-lg touch-none"
            style={{ cursor: 'crosshair', display: 'block' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
            aria-label="Signature pad — draw your signature here"
          />
          {!hasSig && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <PenLine size={24} className="text-muted-foreground mb-2 opacity-40" />
              <p className="text-sm text-muted-foreground opacity-60">Sign here with mouse or finger</p>
            </div>
          )}
          {hasSig && (
            <div className="absolute bottom-2 right-3 flex items-center gap-1 text-xs text-positive opacity-70">
              <CheckCircle size={11} />
              Signature captured
            </div>
          )}
        </div>

        {!hasSig && (
          <p className="field-hint mt-2 flex items-center gap-1">
            <AlertTriangle size={11} className="text-warning" />
            Signature required before submission — the PDF will not generate without it
          </p>
        )}
      </div>

      {/* PDF filing info */}
      <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 text-xs">
        <p className="font-semibold text-primary mb-1 flex items-center gap-1.5">
          <FileText size={12} />
          PDF Filing Path
        </p>
        <p className="font-mono-data text-muted-foreground">
          {branch || '[Branch]'} / {vehicleReg || '[Vehicle Reg]'} / Saved Job Cards / {jobNumber}.pdf
        </p>
        <p className="text-muted-foreground mt-1">
          The folder will be created automatically if it does not exist.
        </p>
      </div>
    </div>
  );
}